import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import { AgreementType, IDeeplinkParameters } from 'fitbeat.models';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { Keyboard, ScrollView, StyleSheet, Text, View } from 'react-native';
import { usesMetricSystem } from 'react-native-localize';
import { connect } from 'react-redux';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { Button, ButtonSize } from '../components';
import { getApiConfig } from '../config';
import { GoogleAnalytics } from '../services/google-analytics';
import Theme from '../theme';
import { FormError, FormKeyboardSpacer, showError, TextInput } from '../ui';
import { defaultScale, UserDataConsumer } from '../utils';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import firebaseErrorHandler from '../utils/FirebaseErrors';
import { focusOnInput } from '../utils/FocusOnInput';
import { clearDeepLinkParams as clearDeepLinkParamsAction } from '../utils/notifications/actions';
import { isEmailValid, isPasswordSame, isRequired, minLength6 } from '../utils/validator';
import { AlreadyHaveAnAccount, TermsAndConditions } from './components';
import { buildSignupSource } from './components/create-platform-user';

interface IState {
  isLoading: boolean;
  allowEditEmail: boolean;
  termsAccepted: boolean;
}

interface IProps {
  basicUserInfo: any;
  initialize: any;
  user: any;
  navigation: {
    navigate: (route: string) => void;
  };
  userCompletingExistingAccount: boolean;
  clearDeepLinkParams: (params: string[]) => void;
  continueSignupToken?: string;
  deepLinkParams: IDeeplinkParameters;
  error?: Error;
  handleSubmit: any;
  t: (key: string) => string;
}

class SignUp extends Component<IProps, IState> {
  private readonly authenticatedUserServiceClient = new UserServiceClient(
    getApiConfig(), fetch, console.log, new AuthTokenBuilder());
  private readonly unAuthenticatedUserServiceClient = new UserServiceClient(
    getApiConfig(), fetch, console.log, undefined);
  private readonly googleAnalytics = new GoogleAnalytics();

  private passwordInput: any;
  private confirmPasswordInput: any;

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      allowEditEmail: true,
      termsAccepted: false,
    };
  }

  public componentDidMount = async () => {
    this.googleAnalytics.setCurrentScreen('SignUp');
    await this.initFormData();
  }

  private initFormData = async () => {
    const {
      basicUserInfo,
      initialize,
      user,
      deepLinkParams,
      continueSignupToken,
    } = this.props;

    if (!continueSignupToken) {
      const userEmail = (user && user.userProfile && user.userProfile.email) || basicUserInfo.email
        || deepLinkParams.email;
      if (userEmail) {
        initialize({ email: userEmail });
      }
      return;
    }

    try {
      this.setState({ isLoading: true });
      const continueSignupUserProfile =
        await this.unAuthenticatedUserServiceClient.getUserBySignupToken(continueSignupToken);
      if (continueSignupUserProfile && continueSignupUserProfile.email) {
        initialize({ email: continueSignupUserProfile.email });
        this.setState({ allowEditEmail: false });
      }
    } catch (error) {
      const e = error as Error;
      showError(e.message);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private login = () => {
    this.props.navigation.navigate('Login');
  }

  private createFirebaseUser = async (email, password) => {
    try {
      const result = await auth().createUserWithEmailAndPassword(email, password);
      return result.user;
    } catch (error) {
      throw firebaseErrorHandler(error);
    }
  }

  private preAccountFlow = async (values, continueSignupToken) => {
    try {
      const {
        basicUserInfo,
        clearDeepLinkParams,
      } = this.props;
      this.setState({ isLoading: true });
      const email = await this.createFirebaseUserForPreSignupAccount(continueSignupToken);
      const user = auth().currentUser;
      await user.updatePassword(values.password);
      await this.authenticatedUserServiceClient.updateBasicUserInfoAfterInitialAuthSetup({
        ...basicUserInfo,
        email,
      });
      clearDeepLinkParams(['continueSignupToken']);
      this.props.navigation.navigate('HomeNavigator');
    } catch (error) {
      throw new SubmissionError({ _error: error });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private nonPreAccountFlow = async (values) => {
    try {
      const { deepLinkParams: { planId , gymName }} = this.props;
      this.setState({ isLoading: true });

      const user = await this.createFirebaseUser(
        values.email,
        values.password,
      );
      const signupSource = buildSignupSource(this.props.deepLinkParams);
      const userToCreate = {
        uid: user.uid,
        email: user.email,
        ...this.props.basicUserInfo,
        useImperialUnit: !usesMetricSystem(),
        signupSource,
        // Even though we have not subscribed yet, set initial current plan so that stripe account id can be resolved
        currentPlanId: planId,
        gymName,
        legalAgreements: [
          AgreementType.Platform,
          AgreementType.Privacy,
          AgreementType.Membership,
          AgreementType.Home,
        ],
      };

      await this.authenticatedUserServiceClient.createUser(userToCreate);
      this.props.navigation.navigate('HomeNavigator');
    } catch (error) {
      throw new SubmissionError({ _error: error });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private submit = async (values) => {
    this.setState({ isLoading: true });
    const {
      t,
      continueSignupToken,
      user,
    } = this.props;

    if (user && user.userProfile) {
      return await this.completeLoggedInUserSignup(values);
    }

    const preSignupAccountWithEmail = await this.unAuthenticatedUserServiceClient.getUserForPreAccountByEmail(
      values.email);

    if (preSignupAccountWithEmail && !preSignupAccountWithEmail.continueSignupToken) {
      this.setState({ isLoading: false });
      const existingAccountError = new Error(t('existingAccountError'));
      throw new SubmissionError({ _error: existingAccountError });
    }

    if (continueSignupToken || preSignupAccountWithEmail) {
      await this.preAccountFlow(values, continueSignupToken || preSignupAccountWithEmail.continueSignupToken);
    } else {
      await this.nonPreAccountFlow(values);
    }
  }

  private createFirebaseUserForPreSignupAccount = async (continueSignupToken) => {
    if (!continueSignupToken) {
      return;
    }

    const {
      email,
      password,
    } = await this.unAuthenticatedUserServiceClient.continueSignup(continueSignupToken);
    await auth().signInWithEmailAndPassword(email, password);
    return email;
  }

  private completeLoggedInUserSignup = async (values) => {
    const {
      user: {
        userProfileService,
        userProfile,
      },
    } = this.props;
    try {
      this.setState({ isLoading: true });
      const { basicUserInfo } = this.props;
      await this.authenticatedUserServiceClient.updateBasicUserInfoAfterInitialAuthSetup({
        ...basicUserInfo,
        email: userProfile.email,
      });

      const {
        email,
        password,
      } = await this.unAuthenticatedUserServiceClient.continueSignup(userProfile.continueSignupToken);
      await auth().signInWithEmailAndPassword(email, password);
      const user = auth().currentUser;
      await user.updatePassword(values.password);

      await userProfileService.refreshCurrentUserProfile();
      this.props.navigation.navigate('HomeLoading');
    } catch (error) {
      throw new SubmissionError({ _error: error });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private acceptTermsAndConditions = () => {
    this.setState({ termsAccepted: !this.state.termsAccepted });
    Keyboard.dismiss();
  }

  // tslint:disable-next-line:member-ordering
  public render() {
    const {
      t,
      error,
      handleSubmit,
      userCompletingExistingAccount,
      continueSignupToken,
    } = this.props;
    const {
      isLoading,
      allowEditEmail,
      termsAccepted,
    } = this.state;

    return (
      <>
        <View style={styles.container}>
          <FormKeyboardSpacer>
            <ScrollView
              keyboardShouldPersistTaps='handled'
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.scrollViewContainer}>

              <View style={styles.headerContainer}>
                <Text style={styles.header}>
                  {t( continueSignupToken ? 'letsCompleteYourProfile' : 'letsGetStarted')}
                </Text>
              </View>

              <View>
                <Field
                  name='email'
                  returnKeyType='next'
                  autoCapitalize='none'
                  placeholder={t('formFields:email')}
                  component={TextInput}
                  validate={isEmailValid}
                  keyboardType='email-address'
                  editable={allowEditEmail}
                  blurOnSubmit={false}
                  onSubmitEditing={() => focusOnInput(this.passwordInput)}
                />
                <Field
                  forwardRef
                  name='password'
                  secureTextEntry
                  returnKeyType='next'
                  autoCapitalize='none'
                  component={TextInput}
                  placeholder={t('formFields:password')}
                  validate={[isRequired, minLength6]}
                  ref={(ref) => {
                    this.passwordInput = ref;
                  }}
                  blurOnSubmit={false}
                  onSubmitEditing={() => focusOnInput(this.confirmPasswordInput)}
                />
                <Field
                  forwardRef
                  secureTextEntry
                  returnKeyType='done'
                  autoCapitalize='none'
                  component={TextInput}
                  name='confirmPassword'
                  placeholder={t('formFields:confirmPassword')}
                  validate={[isRequired, isPasswordSame]}
                  ref={(ref) => {
                    this.confirmPasswordInput = ref;
                  }}
                  blurOnSubmit
                />
              </View>

              <View style={styles.termsContainer}>
                <TermsAndConditions
                  canAccept={true}
                  preAgreementText={t('byCreatingYouAgreeToTandC')}
                  acceptTermsAndConditions={this.acceptTermsAndConditions}
                  termsAccepted={termsAccepted}
                  textStyle={{ color: Theme.GREY_MEDIUM }}
                />
              </View>

              <FormError color={Theme.GREY_MEDIUM} error={error} />

              <View style={styles.footer}>
                <Button
                  size={ButtonSize.MEDIUM}
                  title={t('signUp')}
                  isLoading={isLoading}
                  fontColor={Theme.WHITE}
                  containerStyle={{
                    marginTop: 3,
                    marginBottom: 10,
                  }}
                  onPress={handleSubmit(this.submit)}
                  disabled={!termsAccepted}
                />
                {!userCompletingExistingAccount &&
                  <AlreadyHaveAnAccount
                    t={t}
                    login={this.login}
                  />
                }
              </View>
            </ScrollView>
          </FormKeyboardSpacer>
        </View>
      </>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 15,
    backgroundColor: Theme.WHITE,
  },
  heading: {
    ...Theme.textStyle.heading,
    paddingTop: 15,
    paddingBottom: 10,
  },
  footer: {
    paddingVertical: 7,
    justifyContent: 'center',
  },
  termsContainer: {
    marginVertical: defaultScale(20),
  },
  scrollViewContainer: {
    flexGrow: 1,
  },
  headerContainer: {
    paddingVertical: 20,
  },
  header: {
    fontSize: 30,
    color: Theme.GREY_DARK,
    textAlign: 'center',
    fontFamily: Theme.Custom800,
  },
});

const mapStateToProps = (state, ownProps: Partial<IProps>) => {
  const continueSignupToken = state.deepLinkParamaters.continueSignupToken;
  const { user } = ownProps;
  const userCompletingExistingAccount = !!(continueSignupToken || user);
  return {
    continueSignupToken,
    basicUserInfo: state.auth.initialUserSetup.basicUserInfo || {},
    userCompletingExistingAccount,
    deepLinkParams: state.deepLinkParamaters,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    clearDeepLinkParams: (keysToClear) => dispatch(clearDeepLinkParamsAction(keysToClear)),
  };
};

export default UserDataConsumer(reduxForm({ form: 'SignUpForm' })
(connect(mapStateToProps, mapDispatchToProps)
(translate(['auth', 'formFields'], { wait: true })(SignUp))));
